import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Plus, Search, Grid, List, Calendar, Clock, Play, Edit3, Trash2, Upload } from 'lucide-react';
import { useProject } from '../context/ProjectContext';

const PageContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 24px 32px;
  border-bottom: 1px solid ${props => props.theme.border};
  background-color: ${props => props.theme.background};
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: ${props => props.theme.text};
  margin: 0;
`;

const ImportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.primary}dd;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const Controls = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 16px 10px 40px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  background-color: ${props => props.theme.surface};
  color: ${props => props.theme.text};
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.primary}20;
  }

  &::placeholder {
    color: ${props => props.theme.textSecondary};
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  color: ${props => props.theme.textSecondary};
`;

const ViewToggle = styled.div`
  display: flex;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  overflow: hidden;
`;

const ViewButton = styled.button`
  padding: 8px 12px;
  border: none;
  background: ${props => props.active ? props.theme.primary : 'transparent'};
  color: ${props => props.active ? 'white' : props.theme.textSecondary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? props.theme.primary : props.theme.surface};
  }
`;

const SortSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background-color: ${props => props.theme.surface};
  color: ${props => props.theme.text};
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const Content = styled.div`
  flex: 1;
  padding: 24px 32px;
  overflow-y: auto;
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

const ProjectsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
`;

const ProjectCard = styled.div`
  background: ${props => props.theme.surface};
  border: 1px solid ${props => props.theme.border};
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadow};
    border-color: ${props => props.theme.primary}50;
  }
`;

const ProjectThumbnail = styled.div`
  width: 100%;
  height: 160px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const PlayIcon = styled(Play)`
  width: 32px;
  height: 32px;
  color: white;
  opacity: 0.8;
`;

const ProjectInfo = styled.div`
  padding: 16px;
`;

const ProjectName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin: 0 0 8px 0;
  line-height: 1.3;
`;

const ProjectMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${props => props.theme.textSecondary};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  background: ${props => {
    switch (props.status) {
      case 'video-only': return props.theme.secondary + '20';
      case 'transcribed': return props.theme.warning + '20';
      case 'translated': return props.theme.success + '20';
      default: return props.theme.secondary + '20';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'video-only': return props.theme.secondary;
      case 'transcribed': return props.theme.warning;
      case 'translated': return props.theme.success;
      default: return props.theme.secondary;
    }
  }};
`;

const ProjectActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: transparent;
  color: ${props => props.theme.text};
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.primary};
    color: white;
    border-color: ${props => props.theme.primary};
  }

  &.danger:hover {
    background: ${props => props.theme.danger};
    border-color: ${props => props.theme.danger};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${props => props.theme.surface};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
`;

const EmptyTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin: 0 0 8px 0;
`;

const EmptyDescription = styled.p`
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  margin: 0 0 24px 0;
  max-width: 400px;
`;

const HomePage = () => {
  const navigate = useNavigate();
  const { projects, loading, createProject, deleteProject } = useProject();
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('date');

  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const handleImportVideo = async () => {
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      // ── Try Local Agent file picker first ─────────────────────────────────
      try {
        const response = await fetch('http://localhost:8001/select-file',
          { signal: AbortSignal.timeout(5000) });
        if (response.ok) {
          const data = await response.json();
          const videoPath = data.path;
          if (videoPath) {
            const fileName = videoPath.split(/[/\\]/).pop().replace(/\.[^/.]+$/, '');
            const project = await createProject(videoPath, fileName, (p) => setUploadProgress(p));
            navigate(`/edit/${project.id}`);
            return; // Exit on success
          }
        }
      } catch (agentError) {
        console.warn('⚠️ Agent unavailable, using browser file picker');
      }

      // ── Browser file input fallback ────────────────────────────────────────
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'video/*';
      input.style.display = 'none';

      const fileSelected = new Promise((resolve) => {
        input.onchange = async (event) => {
          const file = event.target.files[0];
          if (!file) {
            resolve(null);
            return;
          }
          resolve(file);
        };
        // If user cancels, we might never resolve, but input.click() is synchronous
        // and we can't easily detect cancel in all browsers. 
        // We'll set a timeout to clear the overlay if no file is selected after some time.
      });

      document.body.appendChild(input);
      input.click();

      const file = await fileSelected;
      if (document.body.contains(input)) document.body.removeChild(input);

      if (file) {
        const videoUrl = URL.createObjectURL(file);
        const fileName = file.name.replace(/\.[^/.]+$/, '');
        const project = await createProject(
          { file, url: videoUrl, name: file.name, size: file.size, type: file.type },
          fileName,
          (p) => setUploadProgress(p)
        );
        navigate(`/edit/${project.id}`);
      } else {
        setIsUploading(false); // User cancelled or no file selected
      }
    } catch (error) {
      console.error('❌ Upload error:', error);
      alert(`❌ خطأ أثناء رفع الفيديو: ${error.message}`);
      setIsUploading(false);
    } finally {
      // isUploading is usually set to false by navigations or the catch block
      // but we ensure it here as a safety net if we're still on this page
    }
  };

  const handleEditProject = (project) => {
    navigate(`/edit/${project.id}`);
  };

  const handleDeleteProject = async (projectId, event) => {
    event.stopPropagation();
    
    if (window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      try {
        await deleteProject(projectId);
      } catch (error) {
        console.error('Error deleting project:', error);
      }
    }
  };

  const filteredProjects = projects
    .filter(project => 
      project.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'date':
        default:
          return new Date(b.updatedAt) - new Date(a.updatedAt);
      }
    });

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'video-only': return 'Video Only';
      case 'transcribed': return '+ Transcript';
      case 'translated': return '+ Translation';
      default: return 'Video Only';
    }
  };

  return (
    <PageContainer>
      {isUploading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.85)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(8px)'
        }}>
          <div style={{
            width: '400px',
            backgroundColor: '#1a1a1a',
            padding: '40px',
            borderRadius: '24px',
            border: '1px solid #333',
            boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
            textAlign: 'center'
          }}>
            <h2 style={{ color: 'white', marginBottom: '10px', fontSize: '24px' }}>Uploading Video...</h2>
            <p style={{ color: '#888', marginBottom: '30px' }}>Please wait while we process and move your video to the project storage.</p>
            
            <div style={{
              width: '100%',
              height: '10px',
              backgroundColor: '#333',
              borderRadius: '5px',
              overflow: 'hidden',
              marginBottom: '15px'
            }}>
              <div style={{
                width: `${uploadProgress}%`,
                height: '100%',
                backgroundColor: '#007bff',
                transition: 'width 0.3s ease',
                boxShadow: '0 0 15px rgba(0,123,255,0.5)'
              }} />
            </div>
            
            <div style={{ color: 'white', fontSize: '18px', fontWeight: 'bold' }}>
              {uploadProgress}%
            </div>
          </div>
        </div>
      )}
      <Header>
        <HeaderTop>
          <Title>Projects</Title>
          <ImportButton onClick={handleImportVideo}>
            <Plus size={16} />
            Import New Video
          </ImportButton>
        </HeaderTop>
        
        <Controls>
          <SearchContainer>
            <SearchIcon />
            <SearchInput
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchContainer>
          
          <ViewToggle>
            <ViewButton 
              active={viewMode === 'grid'} 
              onClick={() => setViewMode('grid')}
            >
              <Grid size={16} />
            </ViewButton>
            <ViewButton 
              active={viewMode === 'list'} 
              onClick={() => setViewMode('list')}
            >
              <List size={16} />
            </ViewButton>
          </ViewToggle>
          
          <SortSelect value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="date">Sort by Date</option>
            <option value="name">Sort by Name</option>
          </SortSelect>
        </Controls>
      </Header>

      <Content>
        {loading ? (
          <div>Loading projects...</div>
        ) : filteredProjects.length === 0 ? (
          <EmptyState>
            <EmptyIcon>
              <Upload size={32} color="#6c757d" />
            </EmptyIcon>
            <EmptyTitle>No projects yet</EmptyTitle>
            <EmptyDescription>
              Import your first video to get started with BeeVids. 
              You can transcribe, translate, and edit your videos with ease.
            </EmptyDescription>
            <ImportButton onClick={handleImportVideo}>
              <Plus size={16} />
              Import Your First Video
            </ImportButton>
          </EmptyState>
        ) : viewMode === 'grid' ? (
          <ProjectsGrid>
            {filteredProjects.map((project) => (
              <ProjectCard key={project.id} onClick={() => handleEditProject(project)}>
                <ProjectThumbnail>
                  <PlayIcon />
                </ProjectThumbnail>
                <ProjectInfo>
                  <ProjectName>{project.name}</ProjectName>
                  <ProjectMeta>
                    <MetaItem>
                      <Clock />
                      {formatDuration(project.duration)}
                    </MetaItem>
                    <MetaItem>
                      <Calendar />
                      {formatDate(project.updatedAt)}
                    </MetaItem>
                    <StatusBadge status={project.status}>
                      {getStatusLabel(project.status)}
                    </StatusBadge>
                  </ProjectMeta>
                  <ProjectActions>
                    <ActionButton onClick={(e) => { e.stopPropagation(); handleEditProject(project); }}>
                      <Edit3 />
                      Edit
                    </ActionButton>
                    <ActionButton 
                      className="danger"
                      onClick={(e) => handleDeleteProject(project.id, e)}
                    >
                      <Trash2 />
                      Delete
                    </ActionButton>
                  </ProjectActions>
                </ProjectInfo>
              </ProjectCard>
            ))}
          </ProjectsGrid>
        ) : (
          <ProjectsList>
            {/* List view implementation would go here */}
          </ProjectsList>
        )}
      </Content>
    </PageContainer>
  );
};

export default HomePage;